const DEFAULT_WARM_TRANSFER_MAX_ATTEMPTS = 3;

export interface WarmTransferToolFormState {
  enabled: boolean;
  phone_number: string;
  transfer_prompt: string;
  max_transfer_attempts: string;
}

export type VoiceAgentBusinessToolsState = Record<string, boolean>;

export interface VoiceAgentBuiltInToolsState {
  hangupEnabled: boolean;
  formCaptureEnabled: boolean;
  warmTransfer: WarmTransferToolFormState;
  businessTools: VoiceAgentBusinessToolsState;
}

function parsePositiveIntegerOrFallback(value: string, fallback: number): number {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return fallback;
  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isFinite(parsed) || parsed < 1) return fallback;
  return parsed;
}

function readToolsRecord(agent: Record<string, unknown> | undefined | null): Record<string, unknown> | undefined {
  const tools = agent?.tools;
  if (!tools || typeof tools !== 'object') return undefined;
  return tools as Record<string, unknown>;
}

function parseBusinessToolsState(tools: Record<string, unknown> | undefined): VoiceAgentBusinessToolsState {
  const businessToolsRaw = tools?.business_tools;
  if (!Array.isArray(businessToolsRaw)) return {};

  const businessTools: VoiceAgentBusinessToolsState = {};
  for (const item of businessToolsRaw) {
    if (!item || typeof item !== 'object') continue;
    const toolId = (item as { tool_id?: string | number }).tool_id;
    if (toolId == null) continue;
    if ((item as { enabled?: boolean }).enabled !== false) {
      businessTools[String(toolId)] = true;
    }
  }
  return businessTools;
}

export function businessToolsToApiValue(
  businessTools: VoiceAgentBusinessToolsState
): Array<{ tool_id: string; enabled: true }> {
  return Object.entries(businessTools)
    .filter(([, enabled]) => enabled)
    .map(([tool_id]) => ({ tool_id, enabled: true }));
}

function areBusinessToolsEqual(
  left: VoiceAgentBusinessToolsState,
  right: VoiceAgentBusinessToolsState
): boolean {
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  if (leftKeys.length !== rightKeys.length) return false;
  return leftKeys.every((key) => left[key] === right[key]);
}

export function parseVoiceAgentBuiltInToolsState(
  agent: Record<string, unknown> | undefined | null
): VoiceAgentBuiltInToolsState {
  const tools = readToolsRecord(agent);
  const hangupRaw = tools?.hangup_tool;
  const hangupEnabled =
    hangupRaw == null || typeof hangupRaw !== 'object'
      ? true
      : (hangupRaw as { enabled?: boolean }).enabled !== false;

  const formCaptureRaw = tools?.form_capture_tool;
  const formCaptureEnabled =
    formCaptureRaw != null &&
    typeof formCaptureRaw === 'object' &&
    (formCaptureRaw as { enabled?: boolean }).enabled === true;

  const businessTools = parseBusinessToolsState(tools);

  const warmRaw = tools?.Warm_transfer_tool;
  if (!warmRaw || typeof warmRaw !== 'object') {
    return {
      hangupEnabled,
      formCaptureEnabled,
      businessTools,
      warmTransfer: {
        enabled: false,
        phone_number: '',
        transfer_prompt: '',
        max_transfer_attempts: String(DEFAULT_WARM_TRANSFER_MAX_ATTEMPTS),
      },
    };
  }

  const warm = warmRaw as Record<string, unknown>;
  const phoneNumber =
    typeof warm.phone_number === 'string' ? warm.phone_number : String(warm.phone_number ?? '');
  const transferPrompt =
    typeof warm.transfer_prompt === 'string'
      ? warm.transfer_prompt
      : String(warm.transfer_prompt ?? '');
  const maxAttemptsRaw = warm.max_transfer_attempts;
  const maxAttempts =
    typeof maxAttemptsRaw === 'number' || typeof maxAttemptsRaw === 'string'
      ? String(maxAttemptsRaw)
      : String(DEFAULT_WARM_TRANSFER_MAX_ATTEMPTS);

  return {
    hangupEnabled,
    formCaptureEnabled,
    businessTools,
    warmTransfer: {
      enabled: warm.enabled === true,
      phone_number: phoneNumber,
      transfer_prompt: transferPrompt,
      max_transfer_attempts: maxAttempts,
    },
  };
}

export function isWarmTransferConfigured(warmTransfer: WarmTransferToolFormState): boolean {
  return (
    Boolean(warmTransfer.phone_number.trim()) &&
    Boolean(warmTransfer.transfer_prompt.trim()) &&
    Boolean(warmTransfer.max_transfer_attempts.trim())
  );
}

export function buildHangupToolPatchBody(enabled: boolean): { tools: { hangup_tool: { enabled: boolean } } } {
  return { tools: { hangup_tool: { enabled } } };
}

export function buildFormCaptureToolPatchBody(enabled: boolean): {
  tools: { form_capture_tool: { enabled: boolean } };
} {
  return { tools: { form_capture_tool: { enabled } } };
}

export function buildWarmTransferToolPatchBody(warmTransfer: WarmTransferToolFormState): {
  tools: {
    Warm_transfer_tool:
      | {
          enabled: true;
          phone_number: string | null;
          transfer_prompt: string | null;
          max_transfer_attempts: number;
        }
      | null;
  };
} {
  return { tools: { Warm_transfer_tool: warmTransferToApiValue(warmTransfer) } };
}

export function warmTransferToApiValue(
  warmTransfer: WarmTransferToolFormState
):
  | {
      enabled: true;
      phone_number: string | null;
      transfer_prompt: string | null;
      max_transfer_attempts: number;
    }
  | null {
  if (!warmTransfer.enabled) {
    return null;
  }

  return {
    enabled: true,
    phone_number: warmTransfer.phone_number.trim() || null,
    transfer_prompt: warmTransfer.transfer_prompt.trim() || null,
    max_transfer_attempts: parsePositiveIntegerOrFallback(
      warmTransfer.max_transfer_attempts,
      DEFAULT_WARM_TRANSFER_MAX_ATTEMPTS
    ),
  };
}

export function areBuiltInToolsEqual(
  left: VoiceAgentBuiltInToolsState,
  right: VoiceAgentBuiltInToolsState
): boolean {
  if (left.hangupEnabled !== right.hangupEnabled) return false;
  if (left.formCaptureEnabled !== right.formCaptureEnabled) return false;
  if (!areBusinessToolsEqual(left.businessTools, right.businessTools)) return false;
  const leftWarm = left.warmTransfer;
  const rightWarm = right.warmTransfer;
  return (
    leftWarm.enabled === rightWarm.enabled &&
    leftWarm.phone_number === rightWarm.phone_number &&
    leftWarm.transfer_prompt === rightWarm.transfer_prompt &&
    leftWarm.max_transfer_attempts === rightWarm.max_transfer_attempts
  );
}

/** PATCH delta for built-in tools — only changed tool keys are included. */
export function buildBuiltInToolsDeltaPatch(
  current: VoiceAgentBuiltInToolsState,
  previous: VoiceAgentBuiltInToolsState
): { tools?: Record<string, unknown> } {
  const toolsDelta: Record<string, unknown> = {};

  if (current.hangupEnabled !== previous.hangupEnabled) {
    toolsDelta.hangup_tool = { enabled: current.hangupEnabled };
  }

  if (current.formCaptureEnabled !== previous.formCaptureEnabled) {
    toolsDelta.form_capture_tool = { enabled: current.formCaptureEnabled };
  }

  const currentWarm = warmTransferToApiValue(current.warmTransfer);
  const previousWarm = warmTransferToApiValue(previous.warmTransfer);
  if (JSON.stringify(currentWarm) !== JSON.stringify(previousWarm)) {
    toolsDelta.Warm_transfer_tool = currentWarm;
  }

  const currentBusinessTools = businessToolsToApiValue(current.businessTools);
  const previousBusinessTools = businessToolsToApiValue(previous.businessTools);
  if (JSON.stringify(currentBusinessTools) !== JSON.stringify(previousBusinessTools)) {
    toolsDelta.business_tools = currentBusinessTools;
  }

  return Object.keys(toolsDelta).length > 0 ? { tools: toolsDelta } : {};
}
